import * as React from 'react';
import propz from 'propz';
import moment from 'moment';
import * as Yup from 'yup';
import { Field, Form, Formik } from 'formik';
import { DatePicker as SitDatePicker } from 'Src/components/DatePicker/DatePicker';
import { TimePicker } from 'Src/components/TimePicker/TimePicker';
import { AppUser } from '../../../../../App/App';
import { SchoolEvent } from 'Src/models/event';
import { Point } from 'Src/models/venue';
import { EVENT_TYPES } from 'Src/consts/event';
import { EVENT_VENUE, TBD } from 'Src/consts/venue';
import { createVenue } from 'Src/helpers/service/admin/venues';
import { convertPlaceToPostcodeWithPlaceName, getDefaultPoint, getPostcodes, isPlace } from 'Src/helpers/venue/venue';
import { getNameByPlaceOrPostcode } from 'Src/helpers/autocomplete/autocomplete';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { Autocomplete } from 'Src/components/Autocomplete/Autocomplete';
import { VenueForm } from 'Src/views/GenericView/AdminView/School/Venues/VenueForm/VenueForm';
import { isIndividualTournament } from 'Src/helpers/tournament/tournament';
import './SchoolUnionEventEditSettingsForm.scss';

interface Props {
  user: AppUser;
  schoolEvent: SchoolEvent;
  onCancel: () => void;
  onSubmit: (data: any) => void | Promise<void>;
}

interface State {
  point: Point;
  venue: any;
  isCreateVenueFormOpen: boolean;
}

const normaliseEventVenue = (venue: any) => {
  if (!venue) return undefined;
  if (venue.venueType === EVENT_VENUE.TBD) return { ...TBD, postcodeId: TBD.id };
  const placeData: any = propz.get(venue, ['placeData'], {});
  const postcodeData: any = propz.get(venue, ['postcodeData'], {});
  const converted = venue.postcodeId ? convertPlaceToPostcodeWithPlaceName(venue) : venue;

  return {
    ...venue,
    id: venue.postcodeId || postcodeData.id || converted.id,
    postcodeId: venue.postcodeId || postcodeData.id || converted.id,
    placeId: venue.placeId || postcodeData.placeId || placeData.id || converted.placeId,
    name: venue.name || placeData.name || converted.name,
    postcode: venue.postcode || placeData.postcode || postcodeData.postcode || converted.postcode,
    postcodeNoSpaces:
      venue.postcodeNoSpaces ||
      placeData.postcodeNoSpaces ||
      postcodeData.postcodeNoSpaces ||
      converted.postcodeNoSpaces,
    point: venue.point || placeData.point || postcodeData.point || converted.point,
    isHome: typeof venue.isHome === 'boolean' ? venue.isHome : converted.isHome
  };
};

export class SchoolUnionEventEditSettingsForm extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    const { schoolEvent, user } = this.props;
    const venue = normaliseEventVenue(propz.get(schoolEvent, ['venue']));

    this.state = {
      point: propz.get(venue, ['point'], getDefaultPoint(user)),
      venue,
      isCreateVenueFormOpen: false
    };
  }

  applyDatePart = (source: Date, date: Date): Date => {
    const result = new Date(source);
    result.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
    return result;
  };

  applyTimePart = (source: Date, time: Date): Date => {
    const result = new Date(source);
    result.setHours(time.getHours(), time.getMinutes(), 0, 0);
    return result;
  };

  addOneHour = (date: Date): Date => new Date(date.getTime() + 60 * 60 * 1000);

  onSubmitVenueForm = (data, setFieldValue): Promise<void> => {
    const { user } = this.props;

    return createVenue(user, data).then(venue => {
      const convertedVenue = normaliseEventVenue(venue);
      setFieldValue('venue', convertedVenue);
      this.setState({
        venue: convertedVenue,
        point: convertedVenue.point,
        isCreateVenueFormOpen: false
      });
    });
  };

  renderCreateVenueForm = setFieldValue => (
    <SimpleModal
      isOpen={this.state.isCreateVenueFormOpen}
      title="New venue"
      onCloseClick={() => this.setState({ isCreateVenueFormOpen: false })}
      showFooter={false}
      dialogStyle={{ maxWidth: 1120 }}
    >
      <VenueForm
        user={this.props.user}
        onCancel={() => this.setState({ isCreateVenueFormOpen: false })}
        onSubmit={data => this.onSubmitVenueForm(data, setFieldValue)}
      />
    </SimpleModal>
  );

  render() {
    const { schoolEvent, onSubmit, onCancel, user } = this.props;
    const { venue } = this.state;
    const tournament = schoolEvent.tournament || ({} as any);
    const isIndividualEvent = isIndividualTournament(tournament);
    const groups = tournament.customGroups || [];
    const pitches = tournament.pitches || [];

    const validationSchema = Yup.object().shape({
      shortDescription: Yup.string()
        .ensure()
        .max(160, 'Use 160 characters or fewer'),
      startTime: Yup.date()
        .required('Date and start time are required')
        .test('startTime', 'Start time must be earlier than finish time', function(value) {
          return !value || !this.parent.endTime || value < this.parent.endTime;
        }),
      endTime: Yup.date()
        .required('Finish time is required')
        .test('endTime', 'Finish time must be later than start time', function(value) {
          return !value || !this.parent.startTime || value > this.parent.startTime;
        }),
      venue: Yup.mixed().required('Venue is required')
    });

    const initialValues = {
      startTime: moment(schoolEvent.startTime).toDate(),
      endTime: schoolEvent.endTime ? moment(schoolEvent.endTime).toDate() : null,
      venue,
      shortDescription: propz.get(schoolEvent, ['eventDetails', 'shortDescription'], '') || '',
      pitchId: schoolEvent.pitchId || '',
      tournamentGroupId: schoolEvent.tournamentGroupId || '',
      individualEventFormat: schoolEvent.individualEventFormat || {
        category: 'OTHER',
        classification: 'OPEN',
        stage: 'STRAIGHT_FINAL',
        publicNote: ''
      }
    };

    return (
      <div className="eTournamentEventEdit">
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={values => onSubmit(values)}>
          {({ values, setFieldValue, touched, errors, isSubmitting, submitCount }) => (
            <>
              {this.renderCreateVenueForm(setFieldValue)}
              <Form noValidate>
                <fieldset disabled={isSubmitting} className="eTournamentEventEdit__fields">
                  <section className="eTournamentEventEdit__section">
                    <div className="eTournamentEventEdit__field eTournamentEventEdit__fieldFirst">
                      <label htmlFor="fixture-description" className="eTournamentEventEdit__fieldLabel">
                        Display name
                      </label>
                      <Field
                        id="fixture-description"
                        type="text"
                        name="shortDescription"
                        className="eTournamentEventEdit__textInput"
                        placeholder="Optional event name"
                        maxLength={160}
                      />
                      <div className="eTournamentEventEdit__fieldHint">
                        Shown to participating schools and on the tournament website.
                      </div>
                      {touched.shortDescription && errors.shortDescription && (
                        <div className="eTournamentEventEdit__error">{String(errors.shortDescription)}</div>
                      )}
                    </div>

                    <div className="eTournamentEventEdit__dateRow">
                      <Field name="startTime">
                        {({ field }) => (
                          <SitDatePicker
                            label="Date"
                            value={field.value}
                            onChange={(date: Date | null) => {
                              if (!date) return;
                              setFieldValue('startTime', this.applyDatePart(values.startTime, date));
                              if (values.endTime) setFieldValue('endTime', this.applyDatePart(values.endTime, date));
                            }}
                            format="DD MMM YYYY"
                            required
                            clearable={false}
                            centered
                          />
                        )}
                      </Field>
                      <Field name="startTime">
                        {({ field }) => (
                          <TimePicker
                            label="Start time"
                            value={field.value}
                            onChange={(time: Date | null) => {
                              if (!time) return;
                              const nextStart = this.applyTimePart(values.startTime, time);
                              setFieldValue('startTime', nextStart);
                              if (values.endTime && nextStart >= values.endTime) {
                                setFieldValue('endTime', this.addOneHour(nextStart));
                              }
                            }}
                            minuteStep={5}
                            required
                            showConfirm
                            clearable={false}
                            error={touched.startTime && errors.startTime ? String(errors.startTime) : undefined}
                          />
                        )}
                      </Field>
                      <Field name="endTime">
                        {({ field }) => (
                          <TimePicker
                            label="Finish time"
                            value={field.value}
                            onChange={(time: Date | null) => {
                              if (time) {
                                setFieldValue('endTime', this.applyTimePart(values.endTime || values.startTime, time));
                              }
                            }}
                            minuteStep={5}
                            required
                            showConfirm
                            clearable={false}
                            error={touched.endTime && errors.endTime ? String(errors.endTime) : undefined}
                          />
                        )}
                      </Field>
                    </div>
                  </section>

                  <section className="eTournamentEventEdit__section">
                    <div className="eTournamentEventEdit__sectionHeading">
                      <h4>Location</h4>
                    </div>
                    <div className="eTournamentEventEdit__field">
                      <div className="eTournamentEventEdit__labelRow">
                        <label>
                          Venue <span>*</span>
                        </label>
                        <button type="button" onClick={() => this.setState({ isCreateVenueFormOpen: true })}>
                          + New venue
                        </button>
                      </div>
                      <Field name="venue">
                        {() => (
                          <Autocomplete
                            searchFunction={text =>
                              getPostcodes(
                                text,
                                user,
                                schoolEvent.eventType === EVENT_TYPES.EXTERNAL_SCHOOLS,
                                schoolEvent.invitedSchools
                              )
                            }
                            getElementTitle={getNameByPlaceOrPostcode}
                            customClass="mFullWidth eTournamentEventEdit__venueAutocomplete"
                            placeholder="Start typing a venue name or postcode"
                            defaultItem={values.venue}
                            isMenuPortal
                            onSelect={value => {
                              const selectedVenue = normaliseEventVenue(
                                isPlace(value) ? convertPlaceToPostcodeWithPlaceName(value) : value
                              );
                              setFieldValue('venue', selectedVenue);
                              this.setState({ venue: selectedVenue, point: selectedVenue.point });
                            }}
                          />
                        )}
                      </Field>
                      {touched.venue && errors.venue && (
                        <div className="eTournamentEventEdit__error">{String(errors.venue)}</div>
                      )}
                    </div>
                  </section>

                  {!isIndividualEvent && (
                    <section className="eTournamentEventEdit__section">
                      <div className="eTournamentEventEdit__sectionHeading">
                        <h4>Competition</h4>
                      </div>
                      <div className="eTournamentEventEdit__selectRow">
                        <label>
                          <span>Pitch</span>
                          <Field as="select" name="pitchId">
                            <option value="">Not set</option>
                            {pitches.map(pitch => (
                              <option key={pitch.id} value={pitch.id}>
                                {pitch.name}
                              </option>
                            ))}
                          </Field>
                        </label>
                        <label>
                          <span>Group</span>
                          <Field as="select" name="tournamentGroupId">
                            <option value="">Not set</option>
                            {groups.map(group => (
                              <option key={group.id} value={group.id}>
                                {group.name}
                              </option>
                            ))}
                          </Field>
                        </label>
                      </div>
                    </section>
                  )}
                  {isIndividualEvent && (
                    <section className="eTournamentEventEdit__section">
                      <div className="eTournamentEventEdit__sectionHeading">
                        <h4>Competition format</h4>
                      </div>
                      <div className="eTournamentEventEdit__selectRow">
                        <label>
                          <span>Category</span>
                          <Field as="select" name="individualEventFormat.category">
                            <option value="TRACK">Track</option>
                            <option value="FIELD">Field</option>
                            <option value="POOL">Pool</option>
                            <option value="COURSE">Course</option>
                            <option value="COURT">Court</option>
                            <option value="OTHER">Other</option>
                          </Field>
                        </label>
                        <label>
                          <span>Classification</span>
                          <Field as="select" name="individualEventFormat.classification">
                            <option value="OPEN">Open</option>
                            <option value="PARA">Para</option>
                          </Field>
                        </label>
                        <label>
                          <span>Round</span>
                          <Field as="select" name="individualEventFormat.stage">
                            <option value="STRAIGHT_FINAL">Straight final</option>
                            <option value="HEAT">Heat</option>
                            <option value="QUALIFIER">Qualifier</option>
                            <option value="SEMI_FINAL">Semi-final</option>
                            <option value="FINAL">Final</option>
                          </Field>
                        </label>
                      </div>
                      {values.individualEventFormat?.stage === 'HEAT' && (
                        <div className="eTournamentEventEdit__selectRow">
                          <label>
                            <span>Heat number</span>
                            <Field type="number" min="1" name="individualEventFormat.heatNumber" />
                          </label>
                          <label>
                            <span>Total heats</span>
                            <Field type="number" min="1" name="individualEventFormat.heatCount" />
                          </label>
                        </div>
                      )}
                      <div className="eTournamentEventEdit__field">
                        <label className="eTournamentEventEdit__fieldLabel">Public progression note</label>
                        <Field
                          name="individualEventFormat.publicNote"
                          maxLength={500}
                          className="eTournamentEventEdit__textInput"
                          placeholder="For example: fastest six qualify for the final"
                        />
                      </div>
                    </section>
                  )}
                </fieldset>
                <div className="eTournamentEventEdit__actions">
                  {submitCount > 0 && Object.keys(errors).length > 0 && (
                    <div role="alert" className="eTournamentEventEdit__errorSummary">
                      Check the highlighted fields before saving.
                    </div>
                  )}
                  <button type="button" className="is-cancel" onClick={onCancel} disabled={isSubmitting}>
                    Cancel
                  </button>
                  <button type="submit" className="is-save" disabled={isSubmitting}>
                    {isSubmitting ? 'Saving…' : 'Save changes'}
                  </button>
                </div>
              </Form>
            </>
          )}
        </Formik>
      </div>
    );
  }
}
